import { useEffect, useRef, useState, type ReactNode } from "react";
import "./ColumnFilterHead.css";

export type ColumnFilterOption = { value: string; label: string };

/**
 * 표 머리글 하나가 그 열의 조건을 쥔다. 거르는 컨트롤을 표 밖에 따로 두면 그것이 표를
 * 거르는지 다른 것까지 거르는지 자리가 말해 주지 못했고, 열이 여럿이면 밖에 놓인 컨트롤과
 * 열의 짝을 눈으로 이어야 했다. 열 안에 있으면 무엇이 좁아지는지 자명하다.
 */
export function useColumnFilterMenu() {
  const [openKey, setOpenKey] = useState<string | null>(null), [query, setQuery] = useState("");
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { if (openKey) input.current?.focus(); }, [openKey]);
  /* 바깥을 누르면 닫는다. 머리글에 붙은 목록이라 blur만으로는 목록 안을 누를 때도 닫힌다. */
  useEffect(() => {
    if (!openKey) return;
    const dismiss = (event: MouseEvent) => { if (!(event.target as HTMLElement).closest("th")) { setOpenKey(null); setQuery(""); } };
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, [openKey]);
  return { openKey, setOpenKey, query, setQuery, input };
}

/** 검색칸은 컨트롤의 종류가 아니라 목록의 사정이다. 몇 개뿐이면 눈으로 훑는 게 빠르고,
 *  길어지면 그때 돋보기가 목록 위에 선다 — 거는 장치는 어느 열이든 ▾ 하나로 같다. */
const NEEDS_SEARCH = 6;

export function ColumnFilterHead({ label, menu, filterKey, options, value, onChange, sort, children }: {
  label: string;
  menu: ReturnType<typeof useColumnFilterMenu>;
  filterKey?: string;
  options?: ColumnFilterOption[];
  value?: string;
  onChange?: (value: string | undefined) => void;
  /** 정렬을 맡는 열은 이름이 버튼이 된다. */
  sort?: { active: boolean; direction: "ascending" | "descending"; onSort: () => void };
  /** 이름 옆에 붙는 것들 — 효율표 같은 이 열만의 장치. */
  children?: ReactNode;
}) {
  const list = options ?? [];
  const chosen = value ? list.find(option => option.value === value)?.label ?? value : undefined;
  const open = !!filterKey && menu.openKey === filterKey && !chosen;
  const searchable = list.length > NEEDS_SEARCH;
  const matches = searchable && menu.query.trim() ? list.filter(option => option.label.includes(menu.query.trim())) : list;
  const close = () => { menu.setOpenKey(null); menu.setQuery(""); };
  const pick = (next: string | undefined) => { onChange?.(next); close(); };
  return <th scope="col" aria-sort={sort ? (sort.active ? sort.direction : "none") : undefined}>
    <span className="payroll-head">
    {sort
      ? <button type="button" className="payroll-sort" aria-pressed={sort.active} onClick={sort.onSort}>{label}</button>
      : <span className="payroll-head-label">{label}</span>}
    {children}
    {filterKey && onChange && (chosen
      /* 고른 값은 적지 않는다. 걸러 놓으면 그 열의 모든 칸이 같은 값이라 머리글이 한 번 더
         말할 필요가 없다. 푸는 ✕는 ▾가 있던 자리 그대로다. */
      ? <button type="button" className="payroll-worker-search-open" aria-label={`${chosen} 필터 해제`} data-testid={`clear-filter-${filterKey}`}
          onClick={() => pick(undefined)}>✕</button>
      : <button type="button" className="payroll-worker-search-open" aria-expanded={open} aria-label={`${label} 거르기`} data-testid={`open-filter-${filterKey}`}
          onClick={() => { menu.setQuery(""); menu.setOpenKey(current => current === filterKey ? null : filterKey); }}>▾</button>)}
    {open && <div className="payroll-head-menu">
      {/* 검색칸은 목록 위에 선다. 컨트롤 자체를 검색창으로 바꾸면 머리글 폭이 변해 아래 열이
          밀리고, 어느 열은 검색이고 어느 열은 목록인지도 열어 보기 전에는 알 수 없다. */}
      {searchable && <input ref={menu.input} type="search" value={menu.query} placeholder={`${label} 검색`} aria-label={`${label} 검색`} data-testid={`filter-search-${filterKey}`}
        onChange={event => menu.setQuery(event.target.value)}
        onKeyDown={event => { if (event.key === "Escape") close(); if (event.key === "Enter") pick(matches[0]?.value ?? (menu.query.trim() || undefined)); }} />}
      {matches.length > 0
        ? <ul>{matches.slice(0, 40).map(option => <li key={option.value}>
            <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => pick(option.value)}>{option.label}</button>
          </li>)}</ul>
        : <p>찾는 값이 없습니다.</p>}
    </div>}
    </span>
  </th>;
}
